import { ChevronDownIcon } from '@chakra-ui/icons';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useState } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import { UpgradeCta } from '@/features/billing/components/UpgradeCta';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import {
  AddCollaboratorSchema,
  type AddCollaboratorDto,
} from '@/features/permissions/dto/invite.dto';
import { emailAsArray } from '@/features/permissions/lib/collaboratorForm.lib';
import { useCollaboratorLimit } from '@/features/permissions/lib/useCollaboratorLimit';
import type { Collaborator } from '@/features/permissions/types';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { Role } from '@/types/gql.generated';
import {
  useToast,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  Text,
  Textarea,
} from '@/ui';
import { getErrorMessage, isGQLError } from '@/utils/errors';
import { useAddCollaborator } from '../../../hooks/useAddCollaborator';
import { useRemoveCollaborator } from '../../../hooks/useRemoveCollaborator';
import { useUpdateCollaborator } from '../../../hooks/useUpdateCollaborator';
import { RemoveCollaboratorDialog } from './components/RemoveCollaboratorDialog';
import { UserList } from './components/UserList';

type Props = {
  collaborators: Collaborator[];
  defaultEmail?: string;
};

export const CollaboratorsSection = ({
  collaborators,
  defaultEmail = '',
}: Props) => {
  const { addCollaborator, isPending } = useAddCollaborator();
  const { removeCollaborator, isPending: isRemoving } = useRemoveCollaborator();
  const [pendingRemoval, setPendingRemoval] = useState<Collaborator | null>(
    null
  );
  const { updateCollaborator } = useUpdateCollaborator();
  const { notify } = useToast();
  const isMobileBreakpoint = useIsMobileBreakpoint();

  const {
    register,
    control,
    handleSubmit: onSubmit,
    setError: setFieldError,
    reset: resetForm,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: defaultEmail,
      message: '',
      role: Role.Editor,
    },
    resolver: standardSchemaResolver(AddCollaboratorSchema),
  });

  const { remainingSlots, hasReachedLimit, isUpgradable } =
    useCollaboratorLimit(collaborators);
  const { experience } = usePageExperienceContext();

  // Seats are the home org's, so the upgrade that raises the cap is the owner's to
  // buy -- a collaborator who bought Plus would not shift this page's allowance at
  // all. Same call-site owner check as the custom URL in CopyLinkSection, and for the
  // same reason: keep the form and the cap copy, drop only the nudge they can't act
  // on. Guarded on `experience` because a schedule resolves the user-scoped rule,
  // where the allowance really is the viewer's own and the CTA still applies.
  const canUpgradeSeats =
    isUpgradable && (experience == null || experience.role === Role.Owner);

  const handleSubmit: SubmitHandler<AddCollaboratorDto> = (values) => {
    // A second submit while the first is in flight races two invites for the same
    // address through the server, which invites the person twice over.
    if (hasReachedLimit || isPending) {
      return;
    }

    const existingEmails = new Set(
      collaborators.map((c) => c.email?.toLowerCase())
    );
    const seen = new Set<string>();
    const newEmails = emailAsArray(values.email).filter((email) => {
      const lower = email.toLowerCase();
      if (existingEmails.has(lower) || seen.has(lower)) {
        return false;
      }
      seen.add(lower);
      return true;
    });

    if (newEmails.length === 0) {
      setFieldError('email', {
        message: 'All entered emails are already collaborators',
      });
      return;
    }

    const users = newEmails
      .slice(0, remainingSlots ?? newEmails.length)
      .map((email) => ({
        email,
        role: values.role,
      }));

    addCollaborator(
      {
        users,
        message: values.message,
      },
      {
        onError: (err) => {
          setFieldError('email', {
            message: isGQLError(err)
              ? err.response.errors?.[0]?.message
              : 'Error adding this email address',
          });
        },
        onSuccess: () => {
          notify(
            users.length === 1
              ? `"${users[0].email} invited!"`
              : `${users.length} users invited!`
          );
          resetForm({
            email: '',
          });
        },
      }
    );
  };

  return (
    <Flex direction="column" flex="1">
      <Text fontWeight="medium">Invite Collaborators</Text>
      <Text color="customgray.mid" fontSize="sm" mb="3">
        {hasReachedLimit
          ? 'The maximum number of collaborators has been reached'
          : remainingSlots == null
            ? 'Invite people to collaborate on this page'
            : `Invite up to ${remainingSlots} more ${remainingSlots === 1 ? 'person' : 'people'} to collaborate on this page`}
      </Text>

      {/* Only when a bigger plan would actually raise the ceiling. A paid workspace
          already at its cap has nothing left to buy, and a collaborator has nothing
          to buy either -- the seats aren't theirs. */}
      {canUpgradeSeats && (
        <Flex mb="8">
          <UpgradeCta
            feature="collaborators"
            label="Upgrade for more collaborators"
          />
        </Flex>
      )}

      {!hasReachedLimit && (
        <Flex
          as="form"
          direction="column"
          mb="8"
          onSubmit={onSubmit(handleSubmit)}
        >
          <Flex align="start" flex="1" gap="3">
            <FormControl isInvalid={!!errors.email}>
              <Textarea
                as={TextareaAutosize}
                autoFocus={!isMobileBreakpoint}
                placeholder="Email addresses"
                rows={1}
                {...register('email')}
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>

            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <Menu isLazy placement="bottom-end">
                  <MenuButton
                    _active={{ boxShadow: 'none', bg: 'gray.200' }}
                    _hover={{ bg: 'gray.200' }}
                    as={Button}
                    bg="gray.100"
                    border="none"
                    flexShrink="0"
                    rightIcon={<ChevronDownIcon height="4" width="4" />}
                    shadow="none"
                    textTransform="capitalize"
                    variant="secondary"
                  >
                    {(field.value ?? Role.Editor).toLowerCase()}
                  </MenuButton>
                  <Portal>
                    <MenuList minW="9rem" zIndex="var(--chakra-zIndices-modal)">
                      <MenuOptionGroup
                        type="radio"
                        value={field.value}
                        onChange={(value) => field.onChange(value as Role)}
                      >
                        <MenuItemOption value={Role.Viewer}>
                          Viewer
                        </MenuItemOption>
                        <MenuItemOption value={Role.Editor}>
                          Editor
                        </MenuItemOption>
                      </MenuOptionGroup>
                    </MenuList>
                  </Portal>
                </Menu>
              )}
            />

            <Button flexShrink="0" isLoading={isPending} type="submit">
              Invite
            </Button>
          </Flex>
        </Flex>
      )}

      {collaborators.length > 0 && (
        <>
          <Text fontWeight="medium" mb="3">
            People with access
          </Text>
          <UserList
            collaborators={collaborators}
            onChangeRole={(user, role) => updateCollaborator(user.id, role)}
            onRemove={setPendingRemoval}
          />
        </>
      )}

      <RemoveCollaboratorDialog
        isLoading={isRemoving}
        user={pendingRemoval}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (!pendingRemoval) return;
          removeCollaborator(pendingRemoval.id, {
            onSuccess: () => setPendingRemoval(null),
            onError: (error) => notify(getErrorMessage(error)),
          });
        }}
      />
    </Flex>
  );
};
